import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Paper,
  Collapse,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Computer as ComputerIcon,
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  HourglassEmpty as BusyIcon,
  Folder as FolderIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import WorkspaceFilesDialog from "./WorkspaceFilesDialog";
import axios from "axios";
import { hostname } from "../../utils";
import {
  RemoteAgent,
  REMOTE_AGENT_STATUS_ONLINE,
  REMOTE_AGENT_STATUS_OFFLINE,
  REMOTE_AGENT_STATUS_BUSY,
} from "../../constants";

interface RemoteAgentsTabProps {
  getToken: () => Promise<string>;
}

const RemoteAgentsTab: React.FC<RemoteAgentsTabProps> = ({ getToken }) => {
  const [agents, setAgents] = useState<RemoteAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newAgentName, setNewAgentName] = useState("");
  const [newAgentDescription, setNewAgentDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [newAgentApiKey, setNewAgentApiKey] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [deleteConfirmAgent, setDeleteConfirmAgent] = useState<RemoteAgent | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [filesDialogAgent, setFilesDialogAgent] = useState<RemoteAgent | null>(null);
  const [installTab, setInstallTab] = useState(0);

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const response = await axios.get(`${hostname}/remote-agents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAgents(response.data.agents || []);
    } catch (err) {
      console.error("Error fetching remote agents:", err);
      setError("Failed to load workstations");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleCreateAgent = async () => {
    if (!newAgentName.trim()) return;

    try {
      setCreating(true);
      setError(null);
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/remote-agents`,
        {
          name: newAgentName.trim(),
          description: newAgentDescription.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Show the API key (only shown once)
      setNewAgentApiKey(response.data.apiKey);
      setAgents((prev) => [response.data, ...prev]);
    } catch (err) {
      console.error("Error creating agent:", err);
      setError("Failed to create workstation");
    } finally {
      setCreating(false);
    }
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setNewAgentName("");
    setNewAgentDescription("");
    setNewAgentApiKey(null);
    setInstallTab(0);
  };

  const handleDeleteAgent = async () => {
    if (!deleteConfirmAgent) return;

    try {
      setDeleting(true);
      const token = await getToken();
      await axios.delete(`${hostname}/remote-agents/${deleteConfirmAgent.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAgents((prev) => prev.filter((a) => a.id !== deleteConfirmAgent.id));
      setDeleteConfirmAgent(null);
    } catch (err) {
      console.error("Error deleting agent:", err);
      setError("Failed to delete workstation");
    } finally {
      setDeleting(false);
    }
  };

  const handleCopyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case REMOTE_AGENT_STATUS_ONLINE:
        return <OnlineIcon sx={{ color: "success.main", fontSize: 18 }} />;
      case REMOTE_AGENT_STATUS_BUSY:
        return <BusyIcon sx={{ color: "warning.main", fontSize: 18 }} />;
      case REMOTE_AGENT_STATUS_OFFLINE:
      default:
        return <OfflineIcon sx={{ color: "text.disabled", fontSize: 18 }} />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case REMOTE_AGENT_STATUS_ONLINE:
        return "Online";
      case REMOTE_AGENT_STATUS_BUSY:
        return "Busy";
      case REMOTE_AGENT_STATUS_OFFLINE:
      default:
        return "Offline";
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getInstallCommand = (apiKey: string) => {
    const serverUrl = window.location.origin;
    return `curl -sS "${serverUrl}/agent/install.sh?api_key=${apiKey}" | sudo bash`;
  };

  const getNemoClawCommand = (apiKey: string) => {
    const serverUrl = window.location.origin;
    return `curl -sS "${serverUrl}/agent/nemoclaw-install.sh?api_key=${apiKey}" | bash`;
  };

  const getDockerCommand = (apiKey: string) => {
    const serverUrl = window.location.origin;
    return `docker run --rm -it --name ai-agent python:3.12 bash -c 'mkdir -p /opt/agent && curl -sS "${serverUrl}/agent/daemon.py" -o /opt/agent/agent.py && echo "{\\"api_key\\": \\"${apiKey}\\", \\"server_url\\": \\"${serverUrl}\\", \\"poll_interval\\": 5, \\"heartbeat_interval\\": 30}" > /opt/agent/config.json && cd /opt/agent && python3 agent.py'`;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Workstations allow you to execute commands on remote machines through the assistant.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="subtitle2">
          Your Workstations ({agents.length})
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton size="small" onClick={fetchAgents} title="Refresh">
            <RefreshIcon fontSize="small" />
          </IconButton>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Add Workstation
          </Button>
        </Box>
      </Box>

      {agents.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            textAlign: "center",
            backgroundColor: "action.hover",
          }}
        >
          <ComputerIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
          <Typography variant="body2" color="text.secondary" gutterBottom>
            No workstations configured
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add a workstation to execute commands on remote machines
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {agents.map((agent) => (
            <Paper
              key={agent.id}
              variant="outlined"
              sx={{ overflow: "hidden" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 1.5,
                  gap: 1.5,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
                onClick={() => setExpandedAgent(expandedAgent === agent.id ? null : agent.id)}
              >
                {getStatusIcon(agent.status)}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {agent.name}
                  </Typography>
                  {agent.hostname && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {agent.hostname}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={getStatusLabel(agent.status)}
                  size="small"
                  color={agent.status === REMOTE_AGENT_STATUS_ONLINE ? "primary" : "default"}
                  variant={agent.status === REMOTE_AGENT_STATUS_ONLINE ? "filled" : "outlined"}
                />
                <IconButton size="small">
                  {expandedAgent === agent.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={expandedAgent === agent.id}>
                <Box
                  sx={{
                    px: 1.5,
                    pb: 1.5,
                    pt: 0,
                    borderTop: 1,
                    borderColor: "divider",
                    backgroundColor: "action.hover",
                  }}
                >
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, mt: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Agent Version
                      </Typography>
                      <Typography variant="body2">
                        {agent.agentVersion || "Unknown"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        OS
                      </Typography>
                      <Typography variant="body2">
                        {agent.osType || "Unknown"} {agent.osVersion || ""}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Architecture
                      </Typography>
                      <Typography variant="body2">
                        {agent.architecture || "Unknown"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        IP Address
                      </Typography>
                      <Typography variant="body2">
                        {agent.ipAddress || agent.publicIp || "Unknown"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Last Heartbeat
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(agent.lastHeartbeat)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Python
                      </Typography>
                      <Typography variant="body2">
                        {agent.pythonVersion || "Unknown"}
                      </Typography>
                    </Box>
                  </Box>

                  {agent.description && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Description
                      </Typography>
                      <Typography variant="body2">{agent.description}</Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between" }}>
                    {agent.status === REMOTE_AGENT_STATUS_ONLINE ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FolderIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilesDialogAgent(agent);
                        }}
                      >
                        Browse Files
                      </Button>
                    ) : (
                      <Box />
                    )}
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirmAgent(agent);
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
              </Collapse>
            </Paper>
          ))}
        </Box>
      )}

      {/* Create Agent Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCloseCreateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {newAgentApiKey ? "Workstation Created" : "Add Workstation"}
          <IconButton size="small" onClick={handleCloseCreateDialog}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {newAgentApiKey ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Alert severity="warning">
                Save this API key now - it won't be shown again!
              </Alert>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  API Key
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.5,
                    backgroundColor: "action.hover",
                    borderRadius: 1,
                    fontFamily: "monospace",
                    fontSize: "0.85rem",
                    wordBreak: "break-all",
                  }}
                >
                  <Box sx={{ flex: 1 }}>{newAgentApiKey}</Box>
                  <Tooltip title={copiedId === "apikey" ? "Copied!" : "Copy"}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyToClipboard(newAgentApiKey, "apikey")}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Installation Method
                </Typography>
                <Tabs
                  value={installTab}
                  onChange={(_, v) => setInstallTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
                >
                  <Tab label="Shell Script" />
                  <Tab label="NemoClaw" />
                  <Tab label="Docker" />
                </Tabs>

                {installTab === 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" paragraph>
                      Run this command on your Linux or Mac machine to install the workstation agent as a system service:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        p: 1.5,
                        backgroundColor: "action.hover",
                        borderRadius: 1,
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        wordBreak: "break-all",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>{getInstallCommand(newAgentApiKey)}</Box>
                      <Tooltip title={copiedId === "install" ? "Copied!" : "Copy"}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleCopyToClipboard(getInstallCommand(newAgentApiKey), "install")
                          }
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                )}

                {installTab === 1 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" paragraph>
                      Run the agent in a secure sandboxed environment using{" "}
                      <a href="https://github.com/NVIDIA/OpenShell" target="_blank" rel="noopener noreferrer">
                        NVIDIA OpenShell
                      </a>
                      . No sudo required:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        p: 1.5,
                        backgroundColor: "action.hover",
                        borderRadius: 1,
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        wordBreak: "break-all",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>{getNemoClawCommand(newAgentApiKey)}</Box>
                      <Tooltip title={copiedId === "nemoclaw" ? "Copied!" : "Copy"}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleCopyToClipboard(getNemoClawCommand(newAgentApiKey), "nemoclaw")
                          }
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
                      Installs to <code>/opt/ai-platform-agent</code> and runs inside an OpenShell sandbox with security policies.
                    </Typography>
                  </Box>
                )}

                {installTab === 2 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" paragraph>
                      Run the agent in a Docker container. Great for testing or isolated environments:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        p: 1.5,
                        backgroundColor: "action.hover",
                        borderRadius: 1,
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        wordBreak: "break-all",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>{getDockerCommand(newAgentApiKey)}</Box>
                      <Tooltip title={copiedId === "docker" ? "Copied!" : "Copy"}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleCopyToClipboard(getDockerCommand(newAgentApiKey), "docker")
                          }
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
                      Add <code>-v /path/to/workspace:/workspace</code> to mount a local directory.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <TextField
                label="Workstation Name"
                fullWidth
                value={newAgentName}
                onChange={(e) => setNewAgentName(e.target.value)}
                placeholder="e.g., My MacBook, Production Server"
                autoFocus
              />
              <TextField
                label="Description (optional)"
                fullWidth
                multiline
                rows={2}
                value={newAgentDescription}
                onChange={(e) => setNewAgentDescription(e.target.value)}
                placeholder="What is this workstation for?"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {newAgentApiKey ? (
            <Button variant="contained" onClick={handleCloseCreateDialog}>
              Done
            </Button>
          ) : (
            <>
              <Button onClick={handleCloseCreateDialog}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleCreateAgent}
                disabled={!newAgentName.trim() || creating}
              >
                {creating ? <CircularProgress size={20} /> : "Create"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmAgent}
        onClose={() => setDeleteConfirmAgent(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Workstation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteConfirmAgent?.name}"? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmAgent(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAgent}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Workspace Files Dialog */}
      <WorkspaceFilesDialog
        open={!!filesDialogAgent}
        onClose={() => setFilesDialogAgent(null)}
        agent={filesDialogAgent}
        getToken={getToken}
      />
    </Box>
  );
};

export default RemoteAgentsTab;
